/**
 * Client Followup Planning and Support Page
 * Manages client followups, reminders, and scheduling
 */

import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  Calendar,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Bell,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import {
  getClientFollowups,
  getUpcomingFollowups,
  createFollowup,
  updateFollowupStatus,
  deleteFollowup,
  getFollowupsNeedingReminders,
  markReminderSent,
} from '../services/clientFollowup'
import { formatDate } from '../utils/helpers'

const ClientFollowups = () => {
  const organizationId = useOrganizationId()
  const [followups, setFollowups] = useState([])
  const [upcoming, setUpcoming] = useState([])
  const [needingReminders, setNeedingReminders] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, scheduled, completed, upcoming

  const [newFollowup, setNewFollowup] = useState({
    clientId: '',
    type: 'check_in',
    scheduledDate: '',
    notes: '',
  })

  useEffect(() => {
    if (organizationId) {
      loadData()
      loadClients()
    }
  }, [organizationId, filter])

  const loadData = async () => {
    setLoading(true)
    try {
      const filters = filter !== 'all' ? { status: filter } : {}
      const [followupsData, upcomingData, remindersData] = await Promise.all([
        getClientFollowups(organizationId, null, filters),
        getUpcomingFollowups(organizationId, 7),
        getFollowupsNeedingReminders(organizationId, 1),
      ])

      if (followupsData.success) {
        setFollowups(followupsData.data)
      }
      if (upcomingData.success) {
        setUpcoming(upcomingData.data)
      }
      if (remindersData.success) {
        setNeedingReminders(remindersData.data)
      }
    } catch (error) {
      console.error('Error loading followups:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    try {
      const { supabase } = await import('../services/supabase')
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name')
        .eq('organization_id', organizationId)
        .order('last_name')

      if (!error && data) {
        setClients(data)
      }
    } catch (error) {
      console.error('Error loading clients:', error)
    }
  }

  const handleCreateFollowup = async () => {
    if (!newFollowup.clientId || !newFollowup.scheduledDate) {
      alert('Please fill in all required fields')
      return
    }

    const { supabase } = await import('../services/supabase')
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const result = await createFollowup(organizationId, newFollowup.clientId, {
      type: newFollowup.type,
      scheduledDate: newFollowup.scheduledDate,
      notes: newFollowup.notes,
      createdBy: user?.id,
    })

    if (result.success) {
      setShowCreateModal(false)
      setNewFollowup({
        clientId: '',
        type: 'check_in',
        scheduledDate: '',
        notes: '',
      })
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleUpdateStatus = async (followupId, status) => {
    const result = await updateFollowupStatus(followupId, status, organizationId)
    if (result.success) {
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleSendReminder = async (followupId) => {
    const result = await markReminderSent(followupId, organizationId)
    if (result.success) {
      // TODO: Actually send email/SMS reminder
      alert('Reminder sent!')
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleDelete = async (followupId) => {
    if (!confirm('Are you sure you want to delete this followup?')) return

    const result = await deleteFollowup(followupId, organizationId)
    if (result.success) {
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading followups...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Calendar className="w-8 h-8" />
          Client Followups
        </h1>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
          </select>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Followup
          </Button>
        </div>
      </div>

      {/* Upcoming Followups */}
      {upcoming.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Upcoming (Next 7 Days)
            </h2>
          </div>
          <div className="space-y-2">
            {upcoming.map((followup) => (
              <div
                key={followup.id}
                className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded"
              >
                <div className="flex-1">
                  <p className="font-medium">
                    {followup.clients?.first_name} {followup.clients?.last_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {followup.followup_type.replace(/_/g, ' ')} •{' '}
                    {formatDate(followup.scheduled_date)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!followup.reminder_sent && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendReminder(followup.id)}
                    >
                      <Bell className="w-4 h-4 mr-1" />
                      Send Reminder
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(followup.id, 'completed')}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Complete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* All Followups */}
      <Card>
        <h2 className="text-xl font-semibold mb-4">All Followups</h2>
        {followups.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No followups found</p>
        ) : (
          <div className="space-y-2">
            {followups.map((followup) => (
              <div
                key={followup.id}
                className={`flex items-center justify-between p-3 rounded border ${
                  followup.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : followup.status === 'cancelled'
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex-1">
                  <p className="font-medium">
                    {followup.clients?.first_name} {followup.clients?.last_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {followup.followup_type.replace(/_/g, ' ')} •{' '}
                    {formatDate(followup.scheduled_date)}
                  </p>
                  {followup.notes && (
                    <p className="text-sm text-gray-500 mt-1">{followup.notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      followup.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : followup.status === 'cancelled'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {followup.status}
                  </span>
                  {followup.status === 'scheduled' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateStatus(followup.id, 'completed')}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(followup.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create Followup</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Client</label>
                <Select
                  value={newFollowup.clientId}
                  onChange={(e) => setNewFollowup({ ...newFollowup, clientId: e.target.value })}
                  options={clients.map((c) => ({
                    value: c.id,
                    label: `${c.first_name} ${c.last_name}`,
                  }))}
                  placeholder="Select client"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <Select
                  value={newFollowup.type}
                  onChange={(e) => setNewFollowup({ ...newFollowup, type: e.target.value })}
                  options={[
                    { value: 'examination', label: 'Examination' },
                    { value: 'therapy', label: 'Therapy' },
                    { value: 'session', label: 'Session' },
                    { value: 'check_in', label: 'Check In' },
                    { value: 'reminder', label: 'Reminder' },
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Scheduled Date</label>
                <Input
                  type="datetime-local"
                  value={newFollowup.scheduledDate}
                  onChange={(e) => setNewFollowup({ ...newFollowup, scheduledDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  value={newFollowup.notes}
                  onChange={(e) => setNewFollowup({ ...newFollowup, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateFollowup} className="flex-1">
                  Create
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ClientFollowups

